import {
  ApiError,
  type AuthenticatedTransport,
  requestJson,
  requireStatus,
} from "@groundspark/browser-auth";
import {
  type AdminAuditEvent,
  type AdminInvitation,
  type AdminSummary,
  type AdminUser,
  type Branch,
  type BranchSummary,
  parseBranch,
  parseBranchSummary,
  type ElectoralRegister,
  type Group,
  type GroupSummary,
  parseGroup,
  parseGroupSummary,
  type Page,
  parseAdminAuditEvent,
  parseAdminInvitation,
  parseAdminSummary,
  parseAdminUser,
  parseElectoralRegister,
  parsePage,
  type UserChanges,
} from "./domain";

export class AdminApi {
  public constructor(private readonly authentication: AuthenticatedTransport) {}

  public getSummary(): Promise<AdminSummary> {
    return requestJson(
      this.authentication,
      "/v1/admin/summary",
      parseAdminSummary,
      "administration summary",
    );
  }

  public listGroups(): Promise<readonly GroupSummary[]> {
    return requestJson(
      this.authentication,
      "/v1/admin/groups",
      (value) => {
        if (!Array.isArray(value)) throw new TypeError("a group list is required");
        return value.map(parseGroupSummary);
      },
      "group",
    );
  }

  public createGroup(name: string): Promise<Group> {
    return requestJson(this.authentication, "/v1/admin/groups", parseGroup, "group", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  public renameGroup(group: Group, name: string): Promise<Group> {
    return requestJson(
      this.authentication,
      `/v1/admin/groups/${encodeIdentifier(group.id)}`,
      parseGroup,
      "group",
      {
        method: "PUT",
        body: JSON.stringify({ name, expected_name: group.name }),
      },
    );
  }

  public async deleteGroup(groupId: string): Promise<void> {
    const response = await this.authentication.authenticatedFetch(
      `/v1/admin/groups/${encodeIdentifier(groupId)}`,
      { method: "DELETE" },
    );
    await requireStatus(response, 204);
  }

  public listBranches(): Promise<readonly BranchSummary[]> {
    return requestJson(
      this.authentication,
      "/v1/admin/branches",
      (value) => {
        if (!Array.isArray(value)) throw new TypeError("a branch list is required");
        return value.map(parseBranchSummary);
      },
      "branch",
    );
  }

  public createBranch(name: string): Promise<Branch> {
    return requestJson(
      this.authentication,
      "/v1/admin/branches",
      parseBranch,
      "branch",
      { method: "POST", body: JSON.stringify({ name }) },
    );
  }

  public async deleteBranch(branchId: string): Promise<void> {
    const response = await this.authentication.authenticatedFetch(
      `/v1/admin/branches/${encodeIdentifier(branchId)}`,
      { method: "DELETE" },
    );
    await requireStatus(response, 204);
  }

  public renameBranch(branch: Branch, name: string): Promise<Branch> {
    return requestJson(
      this.authentication,
      `/v1/admin/branches/${encodeIdentifier(branch.id)}`,
      parseBranch,
      "branch",
      { method: "PUT", body: JSON.stringify({ name, expected_name: branch.name }) },
    );
  }

  public updateUser(user: AdminUser, changes: UserChanges): Promise<AdminUser> {
    return requestJson(
      this.authentication,
      `/v1/admin/users/${encodeIdentifier(user.id)}`,
      parseAdminUser,
      "user",
      {
        method: "PUT",
        body: JSON.stringify({
          first_name: changes.firstName,
          last_name: changes.lastName,
          phone_number: changes.phoneNumber,
          email: changes.email,
          branch_id: changes.branchId,
          group_ids: [...changes.groupIds].sort(),
          role: changes.role,
          electoral_register_ids: [...changes.registerIds].sort(),
          expected_access_version: user.accessVersion,
        }),
      },
    );
  }

  public listUsers(cursor: string | null = null): Promise<Page<AdminUser>> {
    return this.getPage("/v1/admin/users", parseAdminUser, cursor);
  }

  public listInvitations(cursor: string | null = null): Promise<Page<AdminInvitation>> {
    return this.getPage("/v1/admin/invitations", parseAdminInvitation, cursor);
  }

  public createInvitation(email: string, branchId: string): Promise<AdminInvitation> {
    return requestJson(
      this.authentication,
      "/v1/admin/invitations",
      parseAdminInvitation,
      "invitation",
      { method: "POST", body: JSON.stringify({ email, branch_id: branchId }) },
    );
  }

  public listAudit(cursor: string | null = null): Promise<Page<AdminAuditEvent>> {
    return this.getPage("/v1/admin/audit", parseAdminAuditEvent, cursor);
  }

  public listElectoralRegisters(): Promise<readonly ElectoralRegister[]> {
    return requestJson(
      this.authentication,
      "/v1/admin/electoral-registers",
      (value) => {
        if (!Array.isArray(value)) throw new TypeError("a roll list is required");
        const registers = value.map(parseElectoralRegister);
        if (
          new Set(registers.map((register) => register.id)).size !== registers.length
        ) {
          throw new TypeError("electoral register IDs must be unique");
        }
        return registers;
      },
      "roll",
    );
  }

  public async revokeInvitation(invitationId: string): Promise<void> {
    const response = await this.authentication.authenticatedFetch(
      `/v1/admin/invitations/${encodeIdentifier(invitationId)}/revoke`,
      { method: "POST" },
    );
    await requireStatus(response, 204);
  }

  private async getPage<T>(
    path: string,
    parser: (value: unknown) => T,
    cursor: string | null,
  ): Promise<Page<T>> {
    const query = new URLSearchParams({ limit: "100" });
    if (cursor !== null) query.set("cursor", cursor);
    return requestJson(
      this.authentication,
      `${path}?${query}`,
      (value) => parsePage(value, parser),
      "administration",
    );
  }
}

function encodeIdentifier(identifier: string): string {
  if (identifier.length === 0 || identifier.includes("/")) {
    throw new ApiError("The object identifier is invalid.");
  }
  return encodeURIComponent(identifier);
}
